'use client';

import { useEffect, useState } from 'react';

export type TocItem = {
  id: string;
  label: string;
  /** Optional depth - 1 for H2 sections, 2 for H3 subsections. Defaults to 1. */
  depth?: 1 | 2;
};

/**
 * Sticky table of contents for long-form blog articles. Vercel/Fly/PlanetScale
 * engineering blog pattern: appears as a right-rail sidebar on large screens,
 * hidden on mobile. The currently-visible section's link gets the accent
 * color via IntersectionObserver.
 *
 * Each heading on the page must have its `id` listed here AND `scroll-mt-24`
 * (or similar) applied to the heading element so deep-link jumps don't tuck
 * under the fixed site header.
 */
export default function TableOfContents({ items }: { items: TocItem[] }) {
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);

  useEffect(() => {
    if (!items.length) return;

    // Reveal headings as the user scrolls past the top 100px of the viewport,
    // and stop tracking once they pass the bottom 40% - keeps the active
    // marker biased toward the heading actually visible above the fold.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.reduce((acc, e) =>
          e.boundingClientRect.top < acc.boundingClientRect.top ? e : acc
        );
        setActiveId(top.target.id);
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: [0, 1] }
    );

    const elements = items
      .map(({ id }) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [items]);

  if (!items.length) return null;

  return (
    <nav aria-label="Table of contents" className="text-xs">
      <p className="font-mono text-accent uppercase tracking-widest mb-4">On this page</p>
      <ul className="space-y-2 border-l border-white/[0.06]">
        {items.map(({ id, label, depth = 1 }) => {
          const isActive = activeId === id;
          return (
            <li key={id} className={depth === 2 ? 'pl-7' : 'pl-4'}>
              <a
                href={`#${id}`}
                className={`block py-0.5 -ml-px border-l transition-colors ${
                  isActive
                    ? 'border-accent text-accent pl-3'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300 pl-3'
                }`}
              >
                {label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
